import classnames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';

import SecondaryLink from '../../components/buttons/SecondaryLink';
import IntlDate from '../../components/formatters/IntlDate';
import { StrapiApiArticle, StrapiApiArticleAttributes } from '../../lib/integrations/strapi/types';
import PaginatedContentRenderer from '../../renderers/PaginatedContentRenderer';
import pageRoutes from '../../router/pageRoutes';

const DEFAULT_ARTICLES_PER_PAGE = 15;

type BlogPostsWidgetProps = {
  articles: StrapiApiArticle[];
};
const BlogPostsWidget: React.FC<BlogPostsWidgetProps> = React.memo((props) => {
  const { articles } = props;
  const [activePage, setActivePage] = useState<number>(0);

  // Calculate the number of items per page so that there are a max number of 10 pages in the pagination controls
  const itemsPerPage = useMemo(() => Math.max(DEFAULT_ARTICLES_PER_PAGE, Math.ceil(articles.length / 10)), [articles]);

  useEffect(() => {
    // Reset active page if the number of articles has changed
    if (activePage * itemsPerPage > articles.length) {
      setActivePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, itemsPerPage]);

  return (
    <PaginatedContentRenderer data={articles} itemsPerPage={itemsPerPage} activePage={activePage} setActivePage={setActivePage}>
      {(article, { isVisible, isLastVisible }) => (
        <article
          key={`article-${article.id}`}
          // Posts on other pages keep their markup and are only hidden, so every post retains a
          // crawlable link from /blog. The border is driven off the last VISIBLE record so the
          // rendered page looks exactly as it did when the other pages were sliced away.
          className={classnames('border-gray py-4', { hidden: !isVisible, 'border-b-1': !isLastVisible })}
        >
          <BlogPostThumbnailCard attributes={article.attributes} />
        </article>
      )}
    </PaginatedContentRenderer>
  );
});
BlogPostsWidget.displayName = 'BlogPostsWidget';
export default BlogPostsWidget;

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

type BlogPostThumbnailCardProps = {
  attributes: StrapiApiArticleAttributes;
};
const BlogPostThumbnailCard: React.FC<BlogPostThumbnailCardProps> = (props) => {
  const { attributes } = props;
  const articleUrl = `${pageRoutes.Blog}/${encodeURIComponent(attributes.page_url)}`;
  return (
    <div className="flex flex-col md:flex-row gap-4">
      <Link href={articleUrl} className="flex items-center justify-center md:w-[300px] xl:w-[400px]">
        <Image width={400} height={100} src={attributes.imageUrl} alt={attributes.imageDescription} className="rounded-xl" />
      </Link>
      <div className="flex flex-col flex-1 gap-4 w-auto">
        <span className="text-gray font-semibold text-xl">
          <IntlDate date={attributes.publishedAt} />
        </span>
        <Link href={articleUrl} className="text-2xl lg:text-h4 leading-none font-bold">
          {attributes.title}
        </Link>
        <SecondaryLink mode="dark" href={articleUrl}>
          See more
        </SecondaryLink>
      </div>
    </div>
  );
};
BlogPostThumbnailCard.displayName = 'BlogPostThumbnailCard';
